import { api } from './client';

/**
 * Generic helpers for Laravel API Resource responses.
 * - List endpoints return { data: [], links, meta } (paginated).
 * - Single endpoints return { data: {} }.
 */
export function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null;
}

export function unwrapList(res) {
  const body = res?.data;
  return {
    items: body?.data ?? [],
    meta: body?.meta ?? null,
    links: body?.links ?? null,
  };
}

/**
 * Approval-workflow responses (submit/approve/reject/publish) wrap the updated
 * resource in `data` and add a top-level `message` + `status`:
 *   { data: {...}, message: "Story approve succeeded.", status: "approved" }
 * Returns { item, message, status } so callers can surface the server copy
 * verbatim. Failures reject with the normalized { status, message, errors }
 * shape produced by the client interceptor (422 carries the server message too).
 */
export function unwrapAction(res) {
  const body = res?.data;
  return {
    item: body?.data ?? null,
    message: body?.message ?? null,
    status: body?.status ?? body?.data?.status ?? null,
  };
}

/** Factory that produces a CRUD + approval-workflow service for a resource. */
export function makeResource(base) {
  return {
    async list(params = {}) {
      const res = await api.get(`/${base}`, { params });
      return unwrapList(res);
    },
    async get(id, params = {}) {
      const res = await api.get(`/${base}/${id}`, { params });
      return unwrap(res);
    },
    async create(payload) {
      const res = await api.post(`/${base}`, payload);
      return unwrap(res);
    },
    async update(id, payload) {
      const res = await api.put(`/${base}/${id}`, payload);
      return unwrap(res);
    },
    async remove(id) {
      await api.delete(`/${base}/${id}`);
      return true;
    },
    // Approval workflow actions -> { item, message, status }
    async submit(id, comment) {
      const res = await api.post(`/${base}/${id}/submit`, { comment });
      return unwrapAction(res);
    },
    /** pending -> approved. `comment` is optional. */
    async approve(id, comment) {
      const res = await api.post(`/${base}/${id}/approve`, { comment });
      return unwrapAction(res);
    },
    /** pending -> rejected. The server REQUIRES a comment (the reason). */
    async reject(id, comment) {
      const res = await api.post(`/${base}/${id}/reject`, { comment });
      return unwrapAction(res);
    },
    /** approved -> published. Call approve() first when the item is pending. */
    async publish(id, comment) {
      const res = await api.post(`/${base}/${id}/publish`, { comment });
      return unwrapAction(res);
    },
  };
}

export const storiesApi = makeResource('stories');
export const eventsApi = makeResource('events');
export const activitiesApi = makeResource('activities');
export const pagesApi = makeResource('pages');

/**
 * Members / Board directory.
 * - Reads are public: list() supports `?leadership=1`, `?club=`, `?per_page=`;
 *   items and get() include `photo_url` (absolute) alongside `photo_path`.
 * - Writes are administrator-only (guest 401, member 403).
 *   create/update accept: club_name, first_name, last_name (required on create),
 *   email, phone, position, is_leadership, bio, joined_on, user_id, and
 *   `photo_media_id` — an integer MediaFile id to (re)attach a photo, explicit
 *   `null` to REMOVE the photo, or ABSENT to leave the photo unchanged.
 *   `photo_path` is not writable.
 * - Administrators additionally see `user_id` and `username` (the linked login
 *   name) on each item; both are absent for other roles. `username` is managed
 *   through createLogin() / setUsername(), never through update().
 */
export const membersApi = {
  async list(params = {}) {
    const res = await api.get('/members', { params });
    return unwrapList(res);
  },
  async get(id, params = {}) {
    const res = await api.get(`/members/${id}`, { params });
    return unwrap(res);
  },
  async create(payload) {
    const res = await api.post('/members', payload);
    return unwrap(res);
  },
  async update(id, payload) {
    const res = await api.put(`/members/${id}`, payload);
    return unwrap(res);
  },
  async remove(id) {
    const res = await api.delete(`/members/${id}`);
    return res?.data ?? { message: 'Deleted' };
  },
  /**
   * POST /api/members/{id}/reset-password (administrator only).
   * Resets the member's LINKED login account. Omit `password` so the backend
   * applies its default (Welcome123). Returns 422 { message } when the member
   * has no linked user account. -> { message, member_id, user_id, used_default_password }.
   */
  async resetPassword(id, password) {
    const body = password ? { password } : {};
    const res = await api.post(`/members/${id}/reset-password`, body);
    return res?.data ?? {};
  },
  /**
   * POST /api/members/{id}/create-login (administrator only).
   * Body { username } is REQUIRED — the admin chooses the login name. The
   * member's email is OPTIONAL, so a member with no email can still be given a
   * login. Username rules (server is the authority): 3-30 chars; letters,
   * numbers, dot, underscore, hyphen; stored lowercase / case-insensitive.
   * -> { message, member_id, user_id, username, created, default_password_used }.
   *
   * 422 responses carry user-facing copy (e.g. the username is taken, or the
   * member already has a login). The client interceptor normalizes it onto
   * `err.message`, so surface that verbatim (same path resetPassword uses).
   */
  async createLogin(id, username) {
    const res = await api.post(`/members/${id}/create-login`, { username });
    return res?.data ?? {};
  },
  /**
   * POST /api/members/{id}/set-username (administrator only).
   * Renames the login of a member that ALREADY has an account. Body { username }.
   * -> { message, member_id, user_id, username, previous_username }.
   * 422 copy (taken / invalid / no linked account) arrives on `err.message`.
   */
  async setUsername(id, username) {
    const res = await api.post(`/members/${id}/set-username`, { username });
    return res?.data ?? {};
  },
  /**
   * Persist the public board display order (administrator only).
   * PUT /api/members/board-order — body { ordered_ids: [id, id, ...] } must be
   * the COMPLETE current leadership list, each id distinct and a leadership
   * member (422 otherwise). Returns the reordered leadership collection in the
   * new order. #1 becomes the featured leader on Meet Area 1 Officers & Presidents.
   */
  async saveBoardOrder(orderedIds) {
    const res = await api.put('/members/board-order', { ordered_ids: orderedIds });
    return unwrapList(res).items;
  },
};

/**
 * Event Recaps ("What happened") — admin-authored recap posts nested under an
 * event. Public reads (published only); admin writes create drafts + publish.
 * `event` and `recap` accept a slug or numeric id (backend uses scoped binding).
 *
 * Reads: list() returns { items, meta, links }. Admins may pass
 * `{ status: 'draft' | 'published' | 'all' }` to list() to see drafts.
 * Writes: create/update accept { title?, body, media_ids?: number[] } — the
 * media_ids array REPLACES the gallery in the given order. Every write via
 * create() produces a draft; publish() flips it live and cascade-publishes media.
 */
export const recapsApi = {
  async list(event, params = {}) {
    const res = await api.get(`/events/${event}/recaps`, { params });
    return unwrapList(res);
  },
  async get(event, recap, params = {}) {
    const res = await api.get(`/events/${event}/recaps/${recap}`, { params });
    return unwrap(res);
  },
  async create(event, payload) {
    const res = await api.post(`/events/${event}/recaps`, payload);
    return unwrap(res);
  },
  async update(event, recap, payload) {
    const res = await api.put(`/events/${event}/recaps/${recap}`, payload);
    return unwrap(res);
  },
  async remove(event, recap) {
    await api.delete(`/events/${event}/recaps/${recap}`);
    return true;
  },
  async publish(event, recap) {
    const res = await api.post(`/events/${event}/recaps/${recap}/publish`);
    return unwrap(res);
  },
};

/**
 * Member Clubs directory.
 * GET /api/clubs (public, no params, not paginated) ->
 *   { data: [ { name, members_count, president: { id, full_name, photo_url } | null } ] }
 * Ordered by club name asc. `president` is null when a club has no President.
 */
export const clubsApi = {
  async list() {
    const res = await api.get('/clubs');
    return res?.data?.data ?? [];
  },
};

export const storyCategoriesApi = {
  /** GET /api/story-categories (public) -> [{ id, name, slug, color }] */
  async list() {
    const res = await api.get('/story-categories');
    return res?.data?.data ?? [];
  },
};

/**
 * Official area News & Highlights — administrator-authored posts published
 * DIRECTLY (no draft->pending->approved->published state machine; that belongs
 * to Stories). Publication is derived from `published_at`.
 *
 * Reads are public and return PUBLISHED items only for guests/members.
 *   - list() supports `?search=` (title/excerpt), `?per_page=` (default 15),
 *     and — for administrators only — `?status=draft|all` to include drafts.
 *   - get() binds by slug (falls back to numeric id); drafts 403 for non-admins.
 * Writes are administrator-only. create/update accept:
 *   { title (req, <=255), body (req), excerpt?, featured_image_path?,
 *     published_at? } — set published_at (date/ISO) to publish, explicit null to
 *   revert to draft. Items expose `featured_image_url` (absolute) for display
 *   alongside the raw `featured_image_path` and derived `is_published`.
 */
export const newsApi = {
  async list(params = {}) {
    const res = await api.get('/news', { params });
    return unwrapList(res);
  },
  async get(idOrSlug, params = {}) {
    const res = await api.get(`/news/${idOrSlug}`, { params });
    return unwrap(res);
  },
  async create(payload) {
    const res = await api.post('/news', payload);
    return unwrap(res);
  },
  async update(id, payload) {
    const res = await api.put(`/news/${id}`, payload);
    return unwrap(res);
  },
  async remove(id) {
    await api.delete(`/news/${id}`);
    return true;
  },
  /**
   * POST /api/news/{id}/submit — author transitions a draft to pending review.
   * Mirrors the Stories/Activities submit action (makeResource.submit).
   */
  async submit(id, comment) {
    const res = await api.post(`/news/${id}/submit`, { comment });
    return unwrapAction(res);
  },
  /**
   * Administrator review actions, identical to the Stories/Activities routes:
   *   approve  POST /api/news/{id}/approve  (pending -> approved)
   *   reject   POST /api/news/{id}/reject   (pending -> rejected, comment REQUIRED)
   *   publish  POST /api/news/{id}/publish  (approved -> published)
   * Each resolves to { item, message, status } with the server message.
   */
  async approve(id, comment) {
    const res = await api.post(`/news/${id}/approve`, { comment });
    return unwrapAction(res);
  },
  async reject(id, comment) {
    const res = await api.post(`/news/${id}/reject`, { comment });
    return unwrapAction(res);
  },
  async publish(id, comment) {
    const res = await api.post(`/news/${id}/publish`, { comment });
    return unwrapAction(res);
  },
  /**
   * POST /api/news/{id}/move (administrator). payload: { target: 'activity'|'story'|'event'|'event_recap',
   * story_category_id?, starts_at?, ends_at?, location?, event_id? }.
   * -> { item, type, message, event? }. A 422 carries the server message
   * (e.g. the item was posted to Facebook from the website and cannot move).
   */
  async move(idOrSlug, payload) {
    const res = await api.post(`/news/${idOrSlug}/move`, payload);
    const body = res?.data;
    return { item: body?.data ?? null, type: body?.type ?? null, message: body?.message ?? null, event: body?.event ?? null };
  },
};

export const menusApi = {
  /** GET /api/menus?location=header|footer (public) -> [{ id, name, location, items: [...] }] */
  async list(location) {
    const res = await api.get('/menus', { params: location ? { location } : {} });
    return res?.data?.data ?? [];
  },
  /** GET /api/menus/{id} -> { id, name, location, items: [...] } */
  async get(id) {
    const res = await api.get(`/menus/${id}`);
    return unwrap(res);
  },
  /** POST /api/menus body { name, location, is_active? } (admin) */
  async create(payload) {
    const res = await api.post('/menus', payload);
    return unwrap(res);
  },
  /** PUT /api/menus/{id} body { name?, location?, is_active?, items:[...] } full-replace (admin) */
  async update(id, payload) {
    const res = await api.put(`/menus/${id}`, payload);
    return unwrap(res);
  },
  /** DELETE /api/menus/{id} (admin) */
  async remove(id) {
    await api.delete(`/menus/${id}`);
    return true;
  },
};

export const calendarApi = {
  async list(params = {}) {
    const res = await api.get('/calendar', { params });
    return unwrapList(res);
  },
  async get(id) {
    const res = await api.get(`/calendar/${id}`);
    return unwrap(res);
  },
};

/**
 * Media Library.
 * MediaFileResource = { id, name, kind, mime_type, size, alt_text, caption,
 *   status, is_published, url, published_at, created_at }.
 *
 *   list(params)          GET  /media?kind=&per_page=  -> { items, meta, links }
 *   get(id)               GET  /media/{id}             -> MediaFileResource
 *   upload(formData)      POST /media (multipart)      -> MediaFileResource
 *   update(id, payload)   PUT  /media/{id}             -> MediaFileResource (metadata only)
 *   publish(id)           POST /media/{id}/publish     -> MediaFileResource (fast-forwards new uploads)
 *   unpublish(id)         POST /media/{id}/unpublish   -> MediaFileResource
 *   usage(id)             GET  /media/{id}/usage       -> { in_use, references:[{type,id,title}] }
 *   remove(id)            DELETE /media/{id}
 */
export const mediaApi = {
  async list(params = {}) {
    const res = await api.get('/media', { params });
    return unwrapList(res);
  },
  async get(id) {
    const res = await api.get(`/media/${id}`);
    return unwrap(res);
  },
  async upload(formData) {
    const res = await api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res);
  },
  async update(id, payload) {
    const res = await api.put(`/media/${id}`, payload);
    return unwrap(res);
  },
  async publish(id) {
    const res = await api.post(`/media/${id}/publish`);
    return unwrap(res);
  },
  async unpublish(id) {
    const res = await api.post(`/media/${id}/unpublish`);
    return unwrap(res);
  },
  async usage(id) {
    // usage() returns a bare { in_use, references } payload (no resource wrapper).
    const res = await api.get(`/media/${id}/usage`);
    return res?.data ?? { in_use: false, references: [] };
  },
  async remove(id) {
    await api.delete(`/media/${id}`);
    return true;
  },
};

export const approvalsApi = {
  async list(params = {}) {
    const res = await api.get('/approvals', { params });
    return unwrapList(res);
  },
  async pending() {
    const res = await api.get('/approvals/pending');
    return res?.data ?? { data: {}, total: 0 };
  },
};

export const dashboardApi = {
  async analytics() {
    const res = await api.get('/dashboard/analytics');
    return res?.data?.data ?? {};
  },
  async recentActivity(limit = 20) {
    const res = await api.get('/dashboard/recent-activity', { params: { limit } });
    return res?.data?.data ?? [];
  },
};
